import React, { useEffect, useState } from 'react';
import {
  Title,
  Icon,
  Text,
  theme,
  Avatar,
  Tooltip,
  ListingImage,
  Select,
  message,
} from 'components';
import styled from 'styled-components';
import {
  ParsedSupraLockboxJsonType,
  updateSupraLockboxStatus,
} from 'api/SupraLockboxController';
import { useHistory } from 'react-router-dom';
import LockboxLicenseTag from 'components/LockboxLicenseTag';
import ResaleStatusTag from 'components/ResaleStatusTag';
import { OwnerAndAssigneeType } from '@bb/domain/dist/types/SupraLockbox';
import { DefaultOptionType } from 'antd/lib/select';
import LockboxStatusTag from 'components/LockboxStatusTag';
import { featureEnabled } from 'utils';
import supraAlternateLogo from '../../../img/SupraAlternateLogo.png';
import AssigneeSearchBar from './AssigneeSearchBar';
import OwnerSearchBar from './OwnerSearchBar';

type HeaderProps = {
  loading?: boolean;
  lockbox?: ParsedSupraLockboxJsonType;
  isMlsUser: boolean;
  fetchLockboxData?: any;
  fetchLockboxActivityData?: any;
  hasPermission?: boolean;
  isLimitedUser?: boolean;
};

const DetailsPageHeader = ({
  loading,
  lockbox,
  isMlsUser,
  hasPermission,
  fetchLockboxData,
  fetchLockboxActivityData,
  isLimitedUser,
}: HeaderProps) => {
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [options, setOptions] = useState<DefaultOptionType[]>([]);
  const { goBack } = useHistory();

  useEffect(() => {
    if (!lockbox?.lockboxStatus) return;
    processOptions(lockbox?.lockboxStatus);
  }, [lockbox]);

  if (!lockbox) return <></>;

  const TooltipText = {
    owner:
      "This user can assign other users to the lockbox, release the shackle, assign this lockbox to listings, and open the lockbox. This box's ownership will be changed to this user.",
    assignee:
      "This user can manage the lockbox, view lockbox activity, release the shackle, assign this lockbox to listings, and open the lockbox. This box's ownership will stay with the current owner.",
    batteryLevel:
      'Battery percentage for this lockbox is shown only when user interacts with the lockbox by opening the lockbox and/or releasing the shackle.',
  };

  const getComponentForBrokerageUser = (roleToCheck: 'owner' | 'assignee') => {
    const [userType, parsedField]: [
      OwnerAndAssigneeType | undefined,
      'parsedOwner' | 'parsedAssignee',
    ] =
      roleToCheck === 'owner'
        ? [lockbox.ownerType, 'parsedOwner']
        : [lockbox.assigneeType, 'parsedAssignee'];
    const props: any = {};

    if (userType === 'user') {
      props.user = {
        firstName: lockbox[parsedField]?.firstName,
        lastName: lockbox[parsedField]?.lastName,
        avatarFilestackId: lockbox[parsedField]?.logo,
      };
    } else {
      props.user = {
        firstName: lockbox[parsedField]?.name,
      };

      props.src = lockbox[parsedField]?.logo;
    }

    return (
      <OwnerContainer>
        <Avatar {...props} />
        <StyledText block>
          {lockbox[parsedField]?.name || 'Unassigned'}
        </StyledText>
      </OwnerContainer>
    );
  };

  const getAssignedListingComponent = () => {
    if (!lockbox.listing) {
      return <ListingContainer>No Listing Assigned</ListingContainer>;
    }

    return (
      <StyledListingContainer>
        <ListingImage imageSrc={lockbox.listing.thumbnail} size={30} />
        <ListingText>
          {lockbox.listing.line1} {lockbox.listing.line2}
        </ListingText>{' '}
        <ListingCityText>
          {lockbox.listing.city}, {lockbox.listing.province}
        </ListingCityText>
      </StyledListingContainer>
    );
  };

  const batteryDisplay = !lockbox.deviceBatteryLevel
    ? 'Unknown'
    : `${lockbox.deviceBatteryLevel.toString()} %`;

  const handleStatusChange = async (status: string) => {
    try {
      setLoadingStatus(true);

      await updateSupraLockboxStatus({
        serialNumber: lockbox?.serialNumber,
        status,
      });
      message.success('Lockbox status updated.');

      await fetchLockboxData(lockbox?._id);
      await fetchLockboxActivityData();
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoadingStatus(false);
    }
  };

  const processOptions = (status: string) => {
    // "New" and "Assigned" have specific behaviors in our system, and should not be
    // modifiable. The other statuses can be switched between.
    switch (status) {
      case 'Used': {
        setOptions([
          { label: 'Lost', value: 'Lost' },
          { label: 'Defective', value: 'Defective' },
          { label: 'Returned', value: 'Returned' },
        ]);
        break;
      }
      case 'Lost': {
        setOptions([
          { label: 'Used', value: 'Used' },
          { label: 'Defective', value: 'Defective' },
          { label: 'Returned', value: 'Returned' },
        ]);
        break;
      }
      case 'Defective': {
        setOptions([
          { label: 'Used', value: 'Used' },
          { label: 'Lost', value: 'Lost' },
          { label: 'Returned', value: 'Returned' },
        ]);
        break;
      }
      case 'Returned': {
        setOptions([
          { label: 'Used', value: 'Used' },
          { label: 'Lost', value: 'Lost' },
          { label: 'Defective', value: 'Defective' },
        ]);
        break;
      }
      default: {
        break;
      }
    }

    return options;
  };

  const disableStatusDropdown = () => {
    if (!lockbox || !lockbox.lockboxStatus) return true;
    return (
      lockbox.lockboxStatus === 'New' || lockbox.lockboxStatus === 'Assigned'
    );
  };

  const renderStatus = () => {
    if (featureEnabled('manageSupraLockboxStatus')) {
      return (
        <LockboxStatusDropdown>
          <p>Status:&nbsp;</p>
          <StyledSelect
            value={lockbox?.lockboxStatus}
            onChange={handleStatusChange}
            options={options}
            loading={loadingStatus}
            disabled={disableStatusDropdown()}
          />
        </LockboxStatusDropdown>
      );
    }
    return (
      <LockboxDetailText>
        Status: <LockboxStatusTag lockboxStatus={lockbox.lockboxStatus} />
      </LockboxDetailText>
    );
  };

  return (
    <LockboxHeader>
      <InfoHeader>
        <LockboxDetailSection>
          <Icon
            type="arrowLeftThin"
            color="black"
            offset={[0, 2]}
            onClick={goBack}
          />
          <SupraLogo src={supraAlternateLogo} />
          <StyledTitle level={1} hideMargin>
            {'Supra Lockbox'}
          </StyledTitle>
          {renderStatus()}
          <LockboxDetailText>
            Supra:{' '}
            <LockboxLicenseTag ownershipStatus={lockbox.ownershipStatus} />
          </LockboxDetailText>
          <LockboxDetailText>
            Resale: <ResaleStatusTag resaleStatus={lockbox.resaleStatus} />
          </LockboxDetailText>
          <LockboxDetailText>
            Serial Number: {lockbox.serialNumber}
          </LockboxDetailText>
          <LockboxDetailText>
            {lockbox.productCode === 'iBox' ? 'iBox BT LE' : 'Invoy'}
          </LockboxDetailText>
          <LockboxDetailText>v{lockbox.version}</LockboxDetailText>
          <LockboxBatteryDetail>
            <BatteryLevelText>Battery Level</BatteryLevelText>
            <Tooltip title={TooltipText.batteryLevel}>
              <StyledIconContainer>
                <i className="fa fa-question-circle"></i>
              </StyledIconContainer>
            </Tooltip>
            : {batteryDisplay}
          </LockboxBatteryDetail>
        </LockboxDetailSection>
      </InfoHeader>
      <LockboxDetailSection>
        <UserSearchSection>
          <LockboxDetailSection>
            <OwnerText>Owner</OwnerText>
            <Tooltip title={TooltipText.owner}>
              <StyledIconContainer>
                <i className="fa fa-question-circle"></i>
              </StyledIconContainer>
            </Tooltip>
          </LockboxDetailSection>

          {isMlsUser || (hasPermission && !isLimitedUser) ? (
            <OwnerSearchBar
              isMlsUser={isMlsUser}
              lockbox={lockbox}
              fetchLockboxData={fetchLockboxData}
            />
          ) : (
            getComponentForBrokerageUser('owner')
          )}
        </UserSearchSection>
        <UserSearchSection>
          <LockboxDetailSection>
            <AssigneeText>Assignee</AssigneeText>
            <Tooltip title={TooltipText.assignee}>
              <StyledIconContainer>
                <i className="fa fa-question-circle"></i>
              </StyledIconContainer>
            </Tooltip>
          </LockboxDetailSection>
          {isMlsUser || (hasPermission && !isLimitedUser) ? (
            <AssigneeSearchBar
              disabled={loading}
              isMlsUser={isMlsUser}
              lockbox={lockbox}
              fetchLockboxData={fetchLockboxData}
            />
          ) : (
            getComponentForBrokerageUser('assignee')
          )}
        </UserSearchSection>
        <UserSearchSection>
          <LockboxDetailSection>
            <AssigneeText>Assigned Listing</AssigneeText>
          </LockboxDetailSection>
          {getAssignedListingComponent()}
        </UserSearchSection>
      </LockboxDetailSection>
    </LockboxHeader>
  );
};

export default DetailsPageHeader;

const StyledText = styled(Text)`
  margin-left: 10px;
`;

const StyledTitle = styled(Title)`
  word-break: normal;
`;

const OwnerContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 0px 10px;
  min-width: 280px;
`;

const InfoHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LockboxHeader = styled.div`
  background: white;
  padding: 10px;
`;

const LockboxDetailSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 5px 0;
  margin-bottom: 5px;
`;

const BatteryLevelText = styled.div`
  font-size: 14px;
  margin-left: 10px;
  margin-right: 3px;
`;

const LockboxBatteryDetail = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 5px 0;
  margin-bottom: 0px;
`;

const UserSearchSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-right: 20px;
`;

const SupraLogo = styled.img`
  height: 50px;
  width: 50px;
  border: 1px solid ${theme.gray3};
  object-fit: contain;
  margin-left: 10px;
  margin-right: 10px;
  border-radius: 100%;
`;

const LockboxDetailText = styled.div`
  margin-left: 10px;
  margin-right: 10px;
`;

const LockboxStatusDropdown = styled.div`
  display: flex;
  margin: 0 10px;
  width: 160px;
  height: 40px;
  line-height: 40px;
`;

const OwnerText = styled.div`
  font-size: 14px;
  font-weight: 800;
  margin-left: 10px;
  margin-right: 10px;
`;

const AssigneeText = styled.div`
  font-size: 14px;
  font-weight: 800;
  margin-left: 10px;
  margin-right: 10px;
`;

const StyledIconContainer = styled.div`
  display: flex;
  color: ${theme.gray6};
  height: 100%;
  align-items: center;
`;

const ListingContainer = styled.div`
  width: 280px;
  height: 40px;
  display: flex;
  align-items: center;
  color: ${theme.gray6};
  padding: 0px 10px;
  margin: 0xp 20px;
  border: 1px solid ${theme.gray2};
  border-radius: 4px;
`;

const StyledListingContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  margin: 0px 10px;
  max-width: 300px;
  gap: 3px;
`;

const ListingText = styled(Text)`
  margin-left: 10px;
  font-weight: 600;
`;

const ListingCityText = styled(Text)`
  color: ${theme.gray7};
`;

const StyledSelect = styled(Select)`
  width: 75%;
`;
